/**
 * Structural types for the Mappedin SDK surface we consume. The App
 * Builder version of this app leans on many internal / loosely-typed
 * methods (`mapView.updateState`, `animateState`, `updateGlobalState`,
 * `manualFloorVisibility`, `mapData.internal.mvf.enterprise.layers`,
 * `registerPlugin`, etc.); rather than augmenting `@mappedin/mappedin-js`
 * globally, we describe exactly the shape we touch and cast the SDK
 * return values through it at the call boundary.
 */

export interface MappedinCoordinate {
  latitude: number;
  longitude: number;
}

export interface MappedinFloor {
  id: string;
  name?: string;
  elevation?: number;
  geoJSON?: unknown;
}

export interface MappedinNode {
  id?: string;
  coordinate?: MappedinCoordinate;
}

export interface MappedinLocation {
  id?: string;
  type?: string;
  amenity?: string;
  name?: string;
  nodes?: MappedinNode[];
  /**
   * Spaces this location is anchored on. v6 Mappedin SDK exposes this
   * as a getter on EnterpriseLocation/LocationProfile. We expose it
   * here so callers can pick a Space anchor for `Markers.add()` (which
   * doesn't accept Locations directly — only Door, Space, Coordinate,
   * or Node).
   */
  spaces?: MappedinSpace[];
}

export interface MappedinSpace {
  id: string;
  name?: string;
  type?: string;
  floor?: MappedinFloor;
  center?: MappedinCoordinate & { latitude: number; longitude: number };
}

export interface MappedinConnection {
  id?: string;
  type?: string;
  connectionType?: string;
  name?: string;
}

export interface MappedinInternalLayerSpace {
  spaceId?: string;
}

export interface MappedinInternalLayer {
  name?: string;
  spaces?: MappedinInternalLayerSpace[];
}

export interface MappedinInternalMvfEnterprise {
  layers?: MappedinInternalLayer[];
}

export interface MappedinInternalMvf {
  enterprise?: MappedinInternalMvfEnterprise;
}

export interface MappedinInternal {
  mvf?: MappedinInternalMvf;
}

export interface MappedinMapData {
  getByType(type: string): unknown[];
  internal?: MappedinInternal;
}

export interface MappedinCameraState {
  zoomLevel?: number;
  pitch?: number;
  bearing?: number;
}

export interface MappedinCamera {
  setScreenOffsets?(offsets: { top: number; bottom: number; left: number; right: number }): void;
  animateTo(target: Record<string, unknown>, options?: Record<string, unknown>): Promise<void>;
  focusOn(targets: unknown[], options?: Record<string, unknown>): Promise<void>;
}

export interface MappedinMarkers {
  add(coordinate: unknown, template: string, options?: Record<string, unknown>): unknown;
  remove(item: unknown): void;
}

export interface MappedinShapes {
  add(geoJson: unknown, options?: Record<string, unknown>, floor?: unknown): unknown;
  remove(shape: unknown): void;
}

export interface MappedinPlugin {
  // Mappedin's plugin contract is duck-typed at runtime; the only
  // requirement is that `registerPlugin(pluginInstance)` accepts it.
  // We keep the interface open so each plugin can declare whatever
  // lifecycle hooks it implements internally.
  readonly [key: string]: unknown;
}

export interface MappedinMapView {
  Camera: MappedinCamera;
  Markers: MappedinMarkers;
  Shapes?: MappedinShapes;
  manualFloorVisibility?: boolean;

  on(event: string, listener: (event: unknown) => void): void;
  off(event: string, listener: (event: unknown) => void): void;

  setFloor(floor: unknown): void;
  getState?(target: unknown): { color?: string } | undefined;
  updateState(target: unknown, state: Record<string, unknown>): void;
  animateState(
    target: unknown,
    state: Record<string, unknown>,
    options?: Record<string, unknown>,
  ): void;
  updateGlobalState(state: Record<string, unknown>): void;

  registerPlugin(plugin: MappedinPlugin): void;

  destroy?(): void;
}

export interface MappedinFloorChangeEvent {
  floor: MappedinFloor;
}

export function isFloorChangeEvent(event: unknown): event is MappedinFloorChangeEvent {
  return (
    typeof event === 'object' &&
    event !== null &&
    'floor' in event &&
    typeof (event as MappedinFloorChangeEvent).floor === 'object'
  );
}

export function isCameraChangeEvent(event: unknown): event is MappedinCameraState {
  return (
    typeof event === 'object' &&
    event !== null &&
    'zoomLevel' in event &&
    typeof (event as MappedinCameraState).zoomLevel === 'number'
  );
}

export interface OutdoorViewOptions {
  token: string;
  style: string;
}
